import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    Card,
    CardContent,
    Chip,
    Divider,
    CircularProgress,
    Alert,
    IconButton,
    Tooltip,
    Button,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    Avatar,
    Badge,
    ListItemSecondaryAction
} from '@mui/material';
import {
    Map as MapIcon,
    Engineering as TechIcon,
    DirectionsCar as CarIcon,
    Refresh as RefreshIcon,
    Phone as PhoneIcon,
    Email as EmailIcon,
    CheckCircle as OnlineIcon,
    Cancel as OfflineIcon,
    AssignmentTurnedIn as ActiveJobsIcon,
    MyLocation as CenterIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import useReliableSocket from '../../hooks/useReliableSocket';

// Solución para iconos por defecto de Leaflet
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerIconShadow from 'leaflet/dist/images/marker-shadow.png';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconUrl: markerIcon,
    shadowUrl: markerIconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
});

// EstilosCSS incrustados para la animación de pulso
const PULSE_STYLE = `
@keyframes pulse-animation {
  0% {
    transform: scale(0.8);
    opacity: 0.8;
  }
  50% {
    opacity: 0.4;
  }
  100% {
    transform: scale(1.4);
    opacity: 0;
  }
}
.pulse-green {
  animation: pulse-animation 2s infinite ease-out;
}
`;

const LiveTechnicianMap = () => {
    const navigate = useNavigate();
    const mapRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const markersLayerRef = useRef(null);
    const markerInstancesMapRef = useRef(new Map()); // Mapear technicianId -> Leaflet marker instance

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [technicians, setTechnicians] = useState([]);
    const [selectedTech, setSelectedTech] = useState(null);

    // Conteo rápido de estados
    const activeTechCount = technicians.filter(t => t.has_active_session).length;
    const offlineTechCount = technicians.length - activeTechCount;

    // Función para procesar coordenadas lat/lng desde DB
    const parseCoords = (lat, lng) => {
        const parsedLat = parseFloat(lat);
        const parsedLng = parseFloat(lng);
        if (!isNaN(parsedLat) && !isNaN(parsedLng) && parsedLat !== 0 && parsedLng !== 0) {
            return [parsedLat, parsedLng];
        }
        return null;
    };

    // Crear icono personalizado con pulso dinámico en SVG
    const createTechMarkerIcon = (isActive) => {
        const color = isActive ? '#4CAF50' : '#9E9E9E'; // Verde si está activo, gris si no
        const html = `
            <div style="position: relative; width: 40px; height: 40px; display: flex; align-items: center; justify-content: center;">
                ${isActive ? `
                <div class="pulse-green" style="
                    position: absolute;
                    width: 48px;
                    height: 48px;
                    background-color: rgba(76, 175, 80, 0.4);
                    border-radius: 50%;
                    z-index: -1;
                "></div>` : ''}
                <div style="
                    width: 32px;
                    height: 32px;
                    background-color: ${color};
                    border-radius: 50%;
                    border: 3px solid #ffffff;
                    box-shadow: 0 3px 6px rgba(0,0,0,0.3);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                ">
                    <svg xmlns="http://www.w3.org/2000/svg" height="18px" viewBox="0 0 24 24" width="18px" fill="#FFFFFF">
                        <path d="M0 0h24v24H0V0z" fill="none"/>
                        <path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm12 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm4-5.5h-3V10h3v3z"/>
                    </svg>
                </div>
            </div>
        `;
        return L.divIcon({
            html: html,
            className: 'leaflet-tech-marker',
            iconSize: [48, 48],
            iconAnchor: [24, 24],
            popupAnchor: [0, -16]
        });
    };

    // Cargar técnicos desde el endpoint backend
    const fetchTechnicians = useCallback(async () => {
        try {
            setError(null);
            const token = localStorage.getItem('token');
            const response = await fetch('/api/map/technicians', {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!response.ok) {
                if (response.status === 401) {
                    window.location.href = '/login';
                    return;
                }
                throw new Error('Error al cargar técnicos');
            }

            const data = await response.json();
            setTechnicians(data);
            return data;
        } catch (err) {
            console.error('Error fetching technicians:', err);
            setError(err.message || 'No se pudieron recuperar los técnicos');
        } finally {
            setLoading(false);
        }
    }, []);

    // Dibujar marcadores en el mapa basándose en el estado de técnicos
    const updateMapMarkers = useCallback((techData) => {
        if (!mapInstanceRef.current || !markersLayerRef.current) return;

        const bounds = [];
        const currentMarkerKeys = new Set(techData.map(t => t.id));

        // Limpiar marcadores que ya no existen
        for (const [techId, marker] of markerInstancesMapRef.current.entries()) {
            if (!currentMarkerKeys.has(techId)) {
                markersLayerRef.current.removeLayer(marker);
                markerInstancesMapRef.current.delete(techId);
            }
        }

        // Crear o actualizar marcadores para cada técnico
        techData.forEach(tech => {
            const coords = parseCoords(tech.latitude, tech.longitude);
            if (coords) {
                bounds.push(coords);

                const popupHtml = `
                    <div style="font-family: 'Inter', sans-serif; min-width: 220px; padding: 5px;">
                        <h4 style="margin: 0 0 8px 0; color: #1e293b; font-size: 14px; font-weight: 600;">
                            ${tech.name}
                        </h4>
                        <div style="display: flex; align-items: center; margin-bottom: 8px;">
                            <span style="
                                display: inline-block;
                                width: 8px;
                                height: 8px;
                                border-radius: 50%;
                                background-color: ${tech.has_active_session ? '#10b981' : '#64748b'};
                                margin-right: 6px;
                            "></span>
                            <span style="font-size: 12px; color: #475569; font-weight: 500;">
                                ${tech.has_active_session ? 'Jornada Activa' : 'Offline / Fuera de Turno'}
                            </span>
                        </div>
                        <p style="margin: 4px 0; font-size: 12px; color: #64748b;">
                            <strong>Tel:</strong> ${tech.phone || 'N/A'}
                        </p>
                        <p style="margin: 4px 0; font-size: 12px; color: #64748b;">
                            <strong>Email:</strong> ${tech.email}
                        </p>
                        <hr style="border: 0; border-top: 1px solid #e2e8f0; margin: 8px 0;" />
                        <div style="display: flex; justify-content: space-between; font-size: 11px;">
                            <span style="background: #e0f2fe; color: #0369a1; padding: 2px 6px; border-radius: 4px; font-weight: 500;">
                                ${tech.active_installations || 0} Inst. Activas
                            </span>
                            <span style="background: #fef3c7; color: #b45309; padding: 2px 6px; border-radius: 4px; font-weight: 500;">
                                ${tech.active_tickets || 0} Soporte
                            </span>
                        </div>
                        ${tech.last_location_update ? `
                        <div style="margin-top: 8px; font-size: 10px; color: #94a3b8; text-align: right;">
                            Sincronizado: ${new Date(tech.last_location_update).toLocaleTimeString()}
                        </div>
                        ` : ''}
                    </div>
                `;

                let existingMarker = markerInstancesMapRef.current.get(tech.id);

                if (existingMarker) {
                    // Si ya existe, actualizamos su posición e ícono
                    existingMarker.setLatLng(coords);
                    existingMarker.setIcon(createTechMarkerIcon(tech.has_active_session));
                    existingMarker.setPopupContent(popupHtml);
                } else {
                    // Si no existe, creamos el marcador
                    const marker = L.marker(coords, {
                        icon: createTechMarkerIcon(tech.has_active_session)
                    })
                    .addTo(markersLayerRef.current)
                    .bindPopup(popupHtml);

                    marker.on('click', () => {
                        setSelectedTech(tech);
                    });

                    markerInstancesMapRef.current.set(tech.id, marker);
                }
            }
        });

        // Auto-ajustar mapa a los marcadores si hay ubicaciones disponibles y no se ha seleccionado ninguno
        if (bounds.length > 0 && !selectedTech && mapInstanceRef.current) {
            mapInstanceRef.current.fitBounds(bounds, { padding: [40, 40], maxZoom: 15 });
        }
    }, [selectedTech]);

    // Inicializar el mapa (SOLO UNA VEZ al montar el componente)
    useEffect(() => {
        if (!mapRef.current || mapInstanceRef.current) return;

        console.log('Montando mapa satelital Leaflet...');
        const osm = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
            maxZoom: 22,
            maxNativeZoom: 19
        });

        const googleHybrid = L.tileLayer('https://{s}.google.com/vt/lyrs=y&x={x}&y={y}&z={z}', {
            subdomains: ['mt0', 'mt1', 'mt2', 'mt3'],
            attribution: '&copy; Google Maps',
            maxZoom: 22,
            maxNativeZoom: 20
        });

        const esriSatellite = L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
            attribution: 'Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community',
            maxZoom: 22,
            maxNativeZoom: 17
        });

        const map = L.map(mapRef.current, {
            center: [-12.0464, -77.0428], // Lima por defecto
            zoom: 12,
            maxZoom: 22,
            layers: [googleHybrid],
            zoomControl: true
        });

        const baseLayers = {
            "Mapa Estándar": osm,
            "Satelital Google (Híbrido)": googleHybrid,
            "Satélite Esri": esriSatellite
        };

        L.control.layers(baseLayers).addTo(map);

        const markersLayer = L.layerGroup().addTo(map);

        mapInstanceRef.current = map;
        markersLayerRef.current = markersLayer;

        // Cargar los técnicos inicialmente
        fetchTechnicians().then(data => {
            if (data) {
                updateMapMarkers(data);
            }
        });

        return () => {
            if (mapInstanceRef.current) {
                console.log('Desmontando mapa satelital Leaflet...');
                mapInstanceRef.current.remove();
                mapInstanceRef.current = null;
            }
        };
    }, [fetchTechnicians]); // fetchTechnicians es estable gracias a useCallback

    // Actualizar marcadores de forma reactiva cuando cambia la lista de técnicos
    useEffect(() => {
        if (mapInstanceRef.current && technicians.length > 0) {
            updateMapMarkers(technicians);
        }
    }, [technicians, updateMapMarkers]);

    // Manejar actualizaciones dinámicas por socket en tiempo real
    const handleLocationUpdate = useCallback((socketPayload) => {
        console.log('Mensaje recibido en LiveTechnicianMap por Socket:', socketPayload);
        const { technicianId, location, timestamp } = socketPayload;

        if (!technicianId || !location) return;

        setTechnicians(prev => {
            const exists = prev.some(t => t.id === technicianId);

            let updatedList;
            if (exists) {
                updatedList = prev.map(tech => {
                    if (tech.id === technicianId) {
                        return {
                            ...tech,
                            latitude: location.latitude,
                            longitude: location.longitude,
                            accuracy: location.accuracy || tech.accuracy,
                            last_location_update: timestamp || new Date().toISOString(),
                            has_active_session: true // Marcar activo si envía coordenadas
                        };
                    }
                    return tech;
                });
            } else {
                // Si no existe, forzamos un refetch completo en background
                fetchTechnicians();
                return prev;
            }

            return updatedList;
        });
    }, [fetchTechnicians]); // handleLocationUpdate ahora es completamente estable

    // Callbacks estables para evitar re-creación de opciones de Socket
    const stableOnConnect = useCallback(() => {
        console.log('Socket de administración conectado con éxito');
    }, []);
    const stableOnDisconnect = useCallback((reason) => {
        console.log('Socket de administración desconectado:', reason);
    }, []);
    const stableOnError = useCallback((err) => {
        console.error('Error de socket de administración:', err);
    }, []);

    const token = localStorage.getItem('token');

    // Configurar Socket.IO con el Hook premium de manera memoizada
    const socketOptions = useMemo(() => ({
        authToken: token,
        type: 'admin',
        enabled: true,
        onConnect: stableOnConnect,
        onDisconnect: stableOnDisconnect,
        onError: stableOnError,
        eventHandlers: {
            'technician_location_updated': handleLocationUpdate
        }
    }), [token, handleLocationUpdate, stableOnConnect, stableOnDisconnect, stableOnError]);

    const { isConnected } = useReliableSocket(socketOptions);

    // Centrar mapa en un técnico
    const centerOnTechnician = (tech) => {
        const coords = parseCoords(tech.latitude, tech.longitude);
        if (coords && mapInstanceRef.current) {
            mapInstanceRef.current.setView(coords, 16);
            setSelectedTech(tech);

            const marker = markerInstancesMapRef.current.get(tech.id);
            if (marker) {
                marker.openPopup();
            }
        }
    };

    return (
        <Box sx={{ 
            p: { xs: 1.5, md: 2 }, 
            height: { xs: 'auto', md: 'calc(100vh - 90px)' }, 
            display: 'flex', 
            flexDirection: 'column', 
            gap: 2, 
            overflow: { xs: 'auto', md: 'hidden' } 
        }}>
            <style dangerouslySetInnerHTML={{ __html: PULSE_STYLE }} />

            <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between', 
                alignItems: { xs: 'stretch', sm: 'center' }, 
                gap: 1.5, 
                flexShrink: 0 
            }}>
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1, color: '#1e293b', fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.65rem' } }}>
                        <TechIcon sx={{ fontSize: { xs: 24, sm: 30 }, color: '#3f51b5' }} />
                        Técnicos en Vivo
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.2, fontSize: '12.5px' }}>
                        Ubicaciones satelitales en tiempo real y estado operativo de las cuadrillas de campo.
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, alignSelf: { xs: 'stretch', sm: 'auto' } }}>
                    <Chip
                        label={isConnected ? "Socket Activo" : "Reconectando..."}
                        color={isConnected ? "success" : "warning"}
                        size="small"
                        sx={{ fontSize: '11px', height: '24px', fontWeight: 600 }}
                    />
                    <Button 
                        variant="outlined" 
                        color="primary"
                        onClick={async () => {
                            setLoading(true);
                            const data = await fetchTechnicians();
                            if (data) updateMapMarkers(data);
                        }}
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon sx={{ fontSize: 16 }} />}
                        sx={{ borderRadius: 2, px: 1.5, py: 0.5, fontSize: '12.5px', height: '28px', textTransform: 'none' }}
                    >
                        Refrescar
                    </Button>
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ borderRadius: 2, py: 0.5 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Resumen rápido compacto */}
            <Paper
                elevation={0}
                sx={{
                    p: 1.5,
                    borderRadius: 2.5,
                    background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
                    color: '#f8fafc',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    flexShrink: 0
                }}
            >
                <Grid container spacing={1} alignItems="center">
                    <Grid item xs={4} sx={{ textAlign: 'center', borderRight: '1px solid rgba(255,255,255,0.12)' }}>
                        <Typography variant="caption" sx={{ color: 'rgba(241, 245, 249, 0.65)', fontSize: '10px', display: 'block', fontWeight: 500 }}>Técnicos Totales</Typography>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#ffffff', fontSize: '13px' }}>{technicians.length}</Typography>
                    </Grid>
                    <Grid item xs={4} sx={{ textAlign: 'center', borderRight: '1px solid rgba(255,255,255,0.12)' }}>
                        <Typography variant="caption" sx={{ color: 'rgba(241, 245, 249, 0.65)', fontSize: '10px', display: 'block', fontWeight: 500 }}>Jornada Activa</Typography>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#10b981', fontSize: '13px' }}>{activeTechCount}</Typography>
                    </Grid>
                    <Grid item xs={4} sx={{ textAlign: 'center' }}>
                        <Typography variant="caption" sx={{ color: 'rgba(241, 245, 249, 0.65)', fontSize: '10px', display: 'block', fontWeight: 500 }}>Fuera de Turno</Typography>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#94a3b8', fontSize: '13px' }}>{offlineTechCount}</Typography>
                    </Grid>
                </Grid>
            </Paper>

            {/* Mapa y Sidebar */}
            <Grid container spacing={2} sx={{ flex: 1, minHeight: 0, height: { xs: 'auto', md: '100%' } }}>
                <Grid item xs={12} md={8} sx={{ height: { xs: '450px', md: '100%' }, display: 'flex', minHeight: 0 }}>
                    <Paper 
                        elevation={0} 
                        sx={{ 
                            position: 'relative', 
                            width: '100%', 
                            height: '100%', 
                            borderRadius: 3, 
                            border: '1px solid #e2e8f0', 
                            overflow: 'hidden',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
                            display: 'flex',
                            flexDirection: 'column'
                        }}
                    >
                        <div ref={mapRef} style={{ height: '100%', width: '100%' }} />
                        {loading && (
                            <Box sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                bgcolor: 'rgba(255,255,255,0.7)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 999
                            }}>
                                <CircularProgress />
                            </Box>
                        )}
                    </Paper>
                </Grid>

                <Grid item xs={12} md={4} sx={{ height: { xs: 'auto', md: '100%' }, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                    <Paper elevation={0} sx={{ p: 2, borderRadius: 2.5, border: '1px solid #e2e8f0', flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 1.5, minHeight: 0, height: { xs: '380px', md: '100%' } }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#334155', fontSize: '13px' }}>
                            Personal Técnico
                        </Typography>
                        <Divider />

                        <Box sx={{ overflowY: 'auto', flex: 1, pr: 0.5, minHeight: 0 }}>
                            {technicians.length === 0 ? (
                                <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                                    No se encontraron técnicos registrados.
                                </Typography>
                            ) : (
                                <List disablePadding>
                                    {technicians.map((tech) => {
                                        const coords = parseCoords(tech.latitude, tech.longitude);
                                        return (
                                            <React.Fragment key={tech.id}>
                                                <ListItem
                                                    alignItems="flex-start"
                                                    button
                                                    onClick={() => centerOnTechnician(tech)}
                                                    selected={selectedTech?.id === tech.id}
                                                    sx={{
                                                        borderRadius: 1,
                                                        mb: 1,
                                                        borderLeft: '4px solid',
                                                        borderColor: tech.has_active_session ? 'success.main' : 'grey.400',
                                                        bgcolor: selectedTech?.id === tech.id ? 'action.selected' : 'transparent',
                                                        '&:hover': {
                                                            bgcolor: 'action.hover'
                                                        }
                                                    }}
                                                >
                                                    <ListItemAvatar>
                                                        <Badge
                                                            overlap="circular"
                                                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                                            variant="dot"
                                                            color={tech.has_active_session ? "success" : "error"}
                                                        >
                                                            <Avatar sx={{ bgcolor: tech.has_active_session ? 'success.light' : 'grey.300', color: tech.has_active_session ? 'success.contrastText' : 'text.secondary' }}>
                                                                <TechIcon />
                                                            </Avatar>
                                                        </Badge>
                                                    </ListItemAvatar>
                                                    <ListItemText
                                                        primary={
                                                            <Typography fontWeight="600" variant="body1" color="text.primary">
                                                                {tech.name}
                                                            </Typography>
                                                        }
                                                        secondary={
                                                            <Box component="span" sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
                                                                <Typography component="span" variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                                                                    <PhoneIcon sx={{ fontSize: 13, mr: 0.5 }} /> {tech.phone || 'Sin número'}
                                                                </Typography>
                                                                {coords ? (
                                                                    <Typography component="span" variant="caption" color="primary.main" sx={{ fontWeight: '500' }}>
                                                                        Ubicación disponible
                                                                    </Typography>
                                                                ) : (
                                                                    <Typography component="span" variant="caption" color="text.disabled">
                                                                        Sin señal GPS
                                                                    </Typography>
                                                                )}
                                                                <Box component="span" sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                                                                    <Chip
                                                                        icon={<ActiveJobsIcon style={{ fontSize: 12, color: 'inherit' }} />}
                                                                        label={`${tech.active_installations || 0} Inst.`}
                                                                        size="small"
                                                                        variant="outlined"
                                                                        sx={{ height: 20, fontSize: '0.65rem' }}
                                                                    />
                                                                    <Chip
                                                                        label={`${tech.active_tickets || 0} Sop.`}
                                                                        size="small"
                                                                        variant="outlined"
                                                                        sx={{ height: 20, fontSize: '0.65rem' }}
                                                                    />
                                                                </Box>
                                                            </Box>
                                                        }
                                                    />
                                                    {coords && (
                                                        <ListItemSecondaryAction>
                                                            <Tooltip title="Ver en mapa">
                                                                <IconButton
                                                                    edge="end"
                                                                    color="primary"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        centerOnTechnician(tech);
                                                                    }}
                                                                >
                                                                    <CenterIcon />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </ListItemSecondaryAction>
                                                    )}
                                                </ListItem>
                                                <Divider variant="inset" component="li" sx={{ my: 0.5, opacity: 0.5 }} />
                                            </React.Fragment>
                                        );
                                    })}
                                </List>
                            )}
                        </Box>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default LiveTechnicianMap;
